/**
 * Small pure helpers shared by more than one Style-It decor planner. Anything
 * used by a single planner lives next to that planner instead.
 */

const {
  isPointInsidePolygon,
} = require('../../bathroom-analyzer/pipeline/geometry');
const { footprintFor } = require('../../geometry/objectFootprint');
const {
  pointToSegmentDistanceXZ,
  findNearestWall,
} = require('../dormDecorGeometry');
const { requiredClearance } = require('./constants');

/** Axis-aligned bbox of a polygon of {x,z} corners. */
function bbox(poly) {
  const xs = poly.map((p) => p.x);
  const zs = poly.map((p) => p.z);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minZ: Math.min(...zs),
    maxZ: Math.max(...zs),
  };
}

/** Whether the room already has an object of any of the given types. */
function hasAnyType(room, types) {
  const present = new Set(
    (room?.objects || [])
      .map((o) => (o?.type || '').toLowerCase())
      .filter(Boolean)
  );
  return types.some((t) => present.has(t));
}

/** Distance from a point {x,z} to the nearest window center, Infinity if none. */
function nearestWindowDist(p, windowCenters) {
  let best = Infinity;
  for (const w of windowCenters || []) {
    const d = Math.hypot(p.x - w.x, p.z - w.z);
    if (d < best) best = d;
  }
  return best;
}

/**
 * True when `point` ({x,z}) is at least `clearDist` from every furniture
 * footprint (rotated corners via `objectFootprint`, distance via
 * pointToSegmentDistanceXZ over each footprint edge).
 */
function isFloorPointClear(point, objects, clearDist) {
  for (const obj of objects) {
    const oc = obj?.center;
    const od = obj?.dimensions;
    if (!oc || !od) continue;
    const corners = footprintFor(
      oc,
      od.x || 0,
      od.z || 0,
      obj?.rotation?.y || 0
    );
    // Point inside the footprint → definitely not clear.
    if (isPointInsidePolygon(point, corners)) return false;
    // A built-in is immovable and needs its access strip kept clear, which is
    // wider than the margin decor normally leaves — a plant 15" from a closet
    // stands in the doorway the optimizer deliberately reserved.
    const required = Math.max(clearDist, requiredClearance(obj));
    for (let i = 0; i < corners.length; i++) {
      const a = corners[i];
      const b = corners[(i + 1) % corners.length];
      if (pointToSegmentDistanceXZ(point, a, b) < required) return false;
    }
  }
  return true;
}

/**
 * Inset a floor-polygon corner toward the centroid along both axes.
 *
 * `|| 1` is load-bearing: a corner exactly level with the centroid on an axis
 * gives Math.sign === 0, which would leave the point ON the wall. This existed
 * three times (hanging planters twice, floor plants once) with a comment on one
 * of them warning that the copies must stay identical — which is the tell that
 * it wanted to be a function.
 */
function insetTowardCentroid(corner, c, inset) {
  return {
    x: corner.x + (Math.sign(c.x - corner.x) || 1) * inset,
    z: corner.z + (Math.sign(c.z - corner.z) || 1) * inset,
  };
}

/**
 * An object's own local frame in the world — THE convention, matching what the
 * renderer actually does.
 *
 * A placed object is drawn inside `<group rotation={[0, y, 0]}>`, and THREE's
 * Y-rotation maps local (lx, lz) to
 *     x = lx·cos + lz·sin
 *     z = −lx·sin + lz·cos
 * Local −Z is therefore the object's BACK: the side against the wall for a desk
 * pushed up to one.
 *
 * This is the TRANSPOSE of the math-standard CCW rotation `calculateCorners`
 * uses, and the two are inverses of each other. They agree exactly at 0° and
 * 180° and are exact POINT REFLECTIONS through the object's centre at ±90° — so
 * the wrong one is invisible on the axis-aligned hosts that dominate a test
 * fixture and 12 inches wrong on every desk against a side wall. That is the
 * bug this replaced (defect 3): knickknacks meant to tuck against the wall sat
 * on the room-facing edge instead, on 1,220 of 2,298 pieces in the dev corpus.
 *
 * `calculateCorners` keeps the other convention on purpose and is correct there:
 * it emits footprint RECTANGLES, which are symmetric, so the handedness cancels.
 * Only non-symmetric points — a single offset like this — expose the difference.
 *
 * @param {{center:{x:number,z:number}, rotation?:{y:number}}} obj  the host
 * @param {number} lx  along the object's own width (local +X)
 * @param {number} lz  local Z; NEGATIVE is toward the object's back
 * @returns {{x:number, z:number}} world position
 */
function objectWorldFromLocal(obj, lx, lz) {
  const rot = obj?.rotation?.y || 0;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  return {
    x: obj.center.x + lx * cos + lz * sin,
    z: obj.center.z - lx * sin + lz * cos,
  };
}

/**
 * The x row of `objectWorldFromLocal`'s exact inverse: where a world point sits
 * along the object's own width.
 *
 * MUST move in lockstep with its forward. The surface packer projects obstacles
 * IN with this and places items OUT with the forward, so a pair that are not
 * true inverses desyncs the free zones from the placements and starts stacking
 * knickknacks on top of each other.
 *
 * @returns {number} distance along local +X from the object's centre
 */
function objectLocalX(obj, point) {
  const rot = obj?.rotation?.y || 0;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  return (point.x - obj.center.x) * cos - (point.z - obj.center.z) * sin;
}

/** Set of room wall ids that carry a DOOR (subset of wallsWithOpenings). */
function doorWallIdSet(room, walls, endpointsById) {
  const ids = new Set();
  for (const door of room?.doors || []) {
    const directIds = door?.parentWallIds || [];
    const matched = directIds.filter((id) => walls.some((w) => w.id === id));
    if (matched.length) {
      for (const id of matched) ids.add(id);
    } else if (door?.center) {
      const w = findNearestWall(door.center, walls, endpointsById);
      if (w) ids.add(w.id);
    }
  }
  return ids;
}

/**
 * True when either room wall touching `corner` (its endpoint coincides with the
 * corner) carries a door. Matches walls to the corner by endpoint proximity.
 */
function cornerTouchesDoor(corner, walls, endpointsById, doorWallIds) {
  const TOL = 0.05; // 5 cm — corner/endpoint coincidence tolerance
  for (const wall of walls) {
    if (!doorWallIds.has(wall.id)) continue;
    const ep = endpointsById.get(wall.id);
    if (!ep?.start || !ep?.end) continue;
    const atStart =
      Math.hypot(corner.x - ep.start.x, corner.z - ep.start.z) < TOL;
    const atEnd = Math.hypot(corner.x - ep.end.x, corner.z - ep.end.z) < TOL;
    if (atStart || atEnd) return true;
  }
  return false;
}

/**
 * Seat up to `count` items in verifiably-empty room corners.
 *
 * Extracted verbatim from the floor-plant emitter so floor DECOR can reuse it.
 * Everything that was hard-coded there is a parameter here — including `rank`,
 * because the plant sorted corners by window proximity ("plants want daylight")
 * and that preference is a plant's, not a pet bed's.
 *
 * Emits nothing rather than force-placing: a corner is used only when neither
 * of its walls carries a door AND the inset point clears every furniture
 * footprint by `clearDist`.
 *
 * @param {object} spec { dims, count, inset, clearDist, rank, build }
 *   `build(point, floorY, cornerIndex)` returns the object to emit.
 * @returns {Array<Object>} entries (≤ count)
 */
function planFloorCornerItems(room, walls, endpointsById, spec, deps) {
  const n = Math.max(0, Math.floor(spec.count || 0));
  if (n === 0) return [];

  const poly = deps.getFloorPolygon(room);
  if (!poly || poly.length === 0) return [];

  const c = deps.centroid(poly);
  const floorY = deps.getFloorY(room);
  const objects = spec.objects || room?.objects || [];
  const doorWallIds = doorWallIdSet(room, walls, endpointsById);

  // Keep the ORIGINAL polygon index alongside each corner: ids are built from
  // it, so sorting must not renumber them.
  const ordered = poly
    .map((corner, ci) => ({ corner, ci }))
    .sort((a, b) => spec.rank(a.corner) - spec.rank(b.corner));

  const entries = [];
  for (const { corner, ci } of ordered) {
    if (entries.length >= n) break;
    if (cornerTouchesDoor(corner, walls, endpointsById, doorWallIds)) continue;

    const point = insetTowardCentroid(corner, c, spec.inset);
    if (!deps.isFloorPointClear(point, objects, spec.clearDist)) continue;

    entries.push(spec.build(point, floorY, ci));
  }

  return entries;
}

/**
 * Build a Style-It decor object.
 *
 * The point of this is `dormProvided: false`. `isStyleItDecor` requires BOTH
 * that flag and type membership, so an emitter that forgets it produces decor
 * the strip cannot see — it survives every re-theme, `hasAnyType` then finds
 * the survivor and emits nothing new, and the object is stuck in the room
 * forever. Ten emitters set it by hand today; new ones should not have to
 * remember.
 */
function decorEntry({
  id,
  type,
  center,
  dimensions,
  rotation = { x: 0, y: 0, z: 0 },
  attributes = {},
  parentWallIds = [],
}) {
  return {
    id,
    type,
    category: { [type]: {} },
    center,
    rotation,
    dimensions,
    story: 0,
    attributes,
    dormProvided: false,
    parentWallIds,
  };
}

module.exports = {
  bbox,
  objectWorldFromLocal,
  objectLocalX,
  hasAnyType,
  nearestWindowDist,
  isFloorPointClear,
  insetTowardCentroid,
  doorWallIdSet,
  cornerTouchesDoor,
  planFloorCornerItems,
  decorEntry,
};
